import React, {
    type ContextType,
    type FC,
    type PropsWithChildren,
    use,
    useCallback,
    useDeferredValue,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from "react";

import { AppConfigUtils } from "@dms/scripts/utils/AppConfigUtils";
import { type ITableColumns } from "@domain/models/dms";
import { type TFilterConfigItem } from "@dms/types";
import { DmsTypesContext } from "@domain/context/DmsDataContexts";
import { DmsUserSettingsContext } from "@dms/types/DmsContextTypes";
import { CompanyContext } from "@domain/context/CompanyContext";
import { UserContext } from "@domain/context/UserProvider";
import { ProgramSettings } from "@domain/models/ProgramSettings";

/**
 Context Provider for User config state
 */
export const DmsUserProvider: FC<PropsWithChildren> = ({ children }) => {
    const [dmsTableEnabledColumns, setDmsTableEnabledColumns] = useState<
        ContextType<typeof DmsUserSettingsContext>["userConfig"]["enabledColumns"]
    >(new Map());
    const [dmsTableColumnWidths, setDmsTableColumnWidths] = useState<
        ContextType<typeof DmsUserSettingsContext>["userConfig"]["columnWidths"]
    >(new Map());
    const [filterConfig, setFilterConfig] = useState<
        ContextType<typeof DmsUserSettingsContext>["userConfig"]["filterConfig"]
    >(new Map());
    const filterConfigDeferred = useDeferredValue(filterConfig);
    const [isTableSize, setIsTableSize] = useState<boolean>(false);
    const { documentTypes } = use(DmsTypesContext);
    const { companyGQL } = use(CompanyContext);
    const { fireUser } = use(UserContext);

    // on mount/documentsType re-fetch column config and merge it with default
    useEffect(() => {
        if (!companyGQL?.id || !fireUser?.uid || documentTypes?.length === 0) {
            return;
        }

        ProgramSettings.getDmsConfig({
            userId: fireUser.uid,
            companyId: companyGQL.id,
        }).then((res) => {
            const { enabledColumns } = res;
            for (const productKey in enabledColumns) {
                if (productKey) {
                    enabledColumns[productKey] = enabledColumns[productKey].map((v) =>
                        v === ("partner" as never) ? "contactId" : v,
                    );
                }
            }
            const concatColumnConf = AppConfigUtils.concatColumnConfig(documentTypes, res.enabledColumns);
            setDmsTableEnabledColumns(concatColumnConf);
            setDmsTableColumnWidths(res.columnWidths);
            setFilterConfig(new Map());
        });
    }, [companyGQL?.id, documentTypes, fireUser?.uid]);

    useLayoutEffect(() => {
        const handleResize = () => {
            setIsTableSize(document.documentElement.clientWidth < 1401);
        };

        handleResize();

        const observer = new ResizeObserver(handleResize);
        observer.observe(document.documentElement);

        return () => observer.disconnect();
    }, []);

    const onSetFilterConfig = useCallback(
        async (typeKey: string[], configData: TFilterConfigItem) => {
            if (!companyGQL?.id || !fireUser?.uid) {
                return;
            }

            const configKey = typeKey.toString();

            setFilterConfig((prev) => {
                const typeConfig = prev.get(configKey);
                prev.set(configKey, JSON.parse(JSON.stringify({ ...typeConfig, ...configData })));

                const paginationConfig = new Map();

                prev.forEach((value, key) => {
                    if (value.paginationState) {
                        paginationConfig.set(key, {
                            paginationState: {
                                pageSize: value.paginationState.pageSize,
                                currentPage: value.paginationState.currentPage,
                            },
                        });
                    }
                });

                // console.log("setFilterConfig", typeKey, configData, new Map(prev));
                // ProgramSettings.setDmsPaginationConfig(keys, Object.fromEntries(paginationConfig));
                return new Map(prev);
            });
        },
        [companyGQL?.id, fireUser?.uid],
    );

    const onUpdateDmsEnabledColumns = useCallback(
        (typeKey: string[], enabledColumns: (keyof ITableColumns)[]) => {
            const configKey = typeKey.toString();
            const keys = {
                userId: fireUser.uid,
                companyId: companyGQL.id,
            };
            ProgramSettings.updateDmsEnabledColumns(keys, configKey, enabledColumns);
            setDmsTableEnabledColumns((prev) => {
                prev.set(configKey, enabledColumns);
                return new Map(prev);
            });
        },
        [fireUser?.uid, companyGQL?.id],
    );

    const setTableColumnWidths = useCallback((typeKey: string[], columnWidth: Map<keyof ITableColumns, number>) => {
        const configKey = typeKey.toString();
        ProgramSettings.updateDmsColumnWidths(configKey, columnWidth);
        setDmsTableColumnWidths((prev) => {
            prev.set(configKey, columnWidth);
            return new Map(prev);
        });
    }, []);

    const deleteTypeConfig = useCallback((typeKey: string[]) => {
        const configKey = typeKey.toString();
        setFilterConfig((prev) => {
            prev.delete(configKey);
            return new Map(prev);
        });
        setDmsTableEnabledColumns((prev) => {
            prev.delete(configKey);
            return new Map(prev);
        });
    }, []);

    const value: ContextType<typeof DmsUserSettingsContext> = useMemo(
        () => ({
            userConfig: {
                enabledColumns: dmsTableEnabledColumns,
                filterConfig: filterConfigDeferred,
                columnWidths: dmsTableColumnWidths,
            },
            setTableEnabledColumns: onUpdateDmsEnabledColumns,
            setFilterConfig: onSetFilterConfig,
            setTableColumnWidths,
            deleteTypeConfig,
            isTableSize,
        }),
        [
            dmsTableEnabledColumns,
            filterConfigDeferred,
            dmsTableColumnWidths,
            onUpdateDmsEnabledColumns,
            onSetFilterConfig,
            setTableColumnWidths,
            deleteTypeConfig,
            isTableSize,
        ],
    );

    return <DmsUserSettingsContext value={value}>{children}</DmsUserSettingsContext>;
};
